import {
  Box,
  Container,
  Grid,
  GridItem,
  Heading,
  List,
  ListItem,
} from '@chakra-ui/react';
import { SharedPageProps } from 'components/Layout';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { colors } from '../../constants/constants';
import AmexLogo from '../../public/images/amex.svg';
import ApplePayLogo from '../../public/images/apple-pay.svg';
import GooglePayLogo from '../../public/images/google-pay.svg';
import KlarnaLogo from '../../public/images/klarna.svg';
import KustomLogo from '../../public/images/kustom-logo.svg';
import Logo from '../../public/images/logo.svg';
import MastercardLogo from '../../public/images/mastercard.svg';
import VisaLogo from '../../public/images/visa.svg';
import FacilityList from '../facility/FacilityList';

export default function Footer({
  facilities,
  facilityTree,
  footerMenu,
  bottomMenu,
  footerText,
  facilitiesArchiveContent,
}: SharedPageProps): JSX.Element {
  const router = useRouter();
  const { pathname } = router;
  const CGrid: any = Grid;
  const CGridItem: any = GridItem;

  useEffect(() => {
    const scriptId = 'weply-widget-script';
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      existingScript.remove(); // Ta bort befintligt skript
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src =
      'https://app.weply.chat/widget/fbd9228dc1f85a67233e4abf1759f167';
    script.async = true;
    document.body.appendChild(script); // Lägg till nytt skript
  }, [pathname]); // Körs varje gång URL:en ändras

  return (
    <Box
      as="footer"
      aria-labelledby="footer-heading"
      bg={colors.white}
      borderTop={`1px solid ${colors.neutrals[100]}`}
      p={{ base: '2em 5%', md: '5em 5%', xl: '3em 0' }}
      marginTop={16}
      sx={{
        '& a': {
          textUnderlineOffset: '0.15em',
        },
        '& a:focus-visible': {
          outline: `3px solid ${colors.neutrals[900]}`,
          outlineOffset: '3px',
        },
      }}>
      <Container maxW="7xl">
        <CGrid>
          <CGridItem>
            <Box as="h2" id="footer-heading" fontSize="2xl" fontWeight="600">
              {facilitiesArchiveContent?.properties.heading || ''}
            </Box>
            <Box
              mb={8}
              dangerouslySetInnerHTML={{
                __html:
                  facilitiesArchiveContent?.properties.ingress?.markup || '',
              }}
            />
            <FacilityList facilityTree={facilityTree} />
          </CGridItem>
        </CGrid>

        <Box
          as="hr"
          borderTop={`1px solid ${colors.neutrals[100]}`}
          mt="2em"
          mb="3em"
        />

        <CGrid
          templateColumns={{
            base: '1fr',
            md: 'repeat(3, minmax(0, 1fr))',
            xl: 'repeat(4, minmax(0, 1fr))',
          }}
          gap={{ base: 8, md: 0 }}
          textAlign={{ base: 'left', md: 'left' }}>
          <CGridItem
            pe={{ base: 0, md: 8, xl: 8 }}
            py={{ base: 0, xl: 1 }}
            alignItems="flex-start"
            justifyContent={{ base: 'center', md: 'flex-start' }}>
            <Box color={colors.white}>
              <Box
                mb={{ base: '3em' }}
                width={{
                  base: '100%',
                  sm: '50%',
                  md: '60%',
                  lg: '80%',
                  xl: '70%',
                }}>
                <Logo fill={colors.custom.primary[900]} width="100%" />
              </Box>
            </Box>
          </CGridItem>

          <CGridItem
            as="section"
            aria-labelledby="footer-contact-heading"
            color={colors.white}
            lineHeight="2"
            pe={{ base: 0, md: 0 }}
            py={{ base: 0, xl: 1 }}>
            <Heading
              as="h4"
              id="footer-contact-heading"
              size="sm"
              fontWeight="600"
              mb={2}
              color={colors.neutrals[900]}>
              Kontakta oss
            </Heading>
            <Box fontSize="md" lineHeight={2}>
              <Box
                maxW="4xl"
                color={colors.neutrals[900]}
                dangerouslySetInnerHTML={{
                  __html: footerText || '',
                }}></Box>
            </Box>
          </CGridItem>

          <CGridItem
            as="nav"
            aria-labelledby="footer-navigation-heading"
            color={colors.white}
            lineHeight="2"
            ps={{ base: 0, md: 0 }}
            py={{ base: 0, xl: 1 }}>
            <Heading
              as="h4"
              id="footer-navigation-heading"
              size="sm"
              fontWeight="600"
              mb={2}
              color={colors.neutrals[900]}>
              Navigation
            </Heading>

            <List>
              {footerMenu?.items?.map((x) => {
                return (
                  <ListItem
                    key={x.path}
                    style={{ transition: '.1s color ease-in-out' }}
                    color={colors.neutrals[900]}>
                    <Link href={`/${x.path}`}>{x.name}</Link>
                  </ListItem>
                );
              })}
            </List>
          </CGridItem>
        </CGrid>

        <Box
          as="section"
          aria-label="Betalningsalternativ"
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          mt={8}>
          <Box width="120px">
            <KustomLogo aria-hidden="true" width="100%" />
          </Box>
          <Box
            display="flex"
            flexWrap="nowrap"
            justifyContent="flex-start"
            alignItems="center"
            gap={0}
            overflowX="auto">
            <KlarnaLogo width={42} height={40} aria-hidden="true" />
            <VisaLogo width={42} height={40} aria-hidden="true" />
            <MastercardLogo width={42} height={40} aria-hidden="true" />
            <AmexLogo width={42} height={40} aria-hidden="true" />
            <ApplePayLogo width={42} height={40} aria-hidden="true" />
            <GooglePayLogo width={42} height={40} aria-hidden="true" />
          </Box>
        </Box>

        <Box
          as="hr"
          borderTop={`1px solid ${colors.neutrals[100]}`}
          mt="2em"
          mb="1em"
        />

        <Box
          display="flex"
          flexDirection={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', md: 'center' }}
          gap={{ base: 2, md: 4 }}
          color={colors.neutrals[900]}
          fontSize="sm">
          <Box>Copyright © Nivika Mittlager AB {new Date().getFullYear()}</Box>
          <List display="flex" flexWrap="wrap" gap={4} m={0}>
            {bottomMenu?.items?.map((item) => (
              <ListItem key={item.path} m={0}>
                <Link href={`/${item.path}`}>{item.name}</Link>
              </ListItem>
            ))}
          </List>
        </Box>
      </Container>
    </Box>
  );
}
